import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface UserCredits {
  credits_balance: number;
  total_credits_used: number;
}

export interface EffectiveCredits {
  credits_balance: number;
  is_team_credits: boolean;
  team_name: string | null;
}

export function useCredits() {
  const { user, isAuthenticated } = useAuth();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [effectiveCredits, setEffectiveCredits] = useState<EffectiveCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user?.id) {
      setCredits(null);
      setEffectiveCredits(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch user's own credits
      const { data: userCredits, error: fetchError } = await supabase
        .from("user_credits")
        .select("credits_balance, total_credits_used")
        .eq("user_id", user.id)
        .single();

      if (fetchError) {
        console.error("Error fetching credits:", fetchError);
        setError(fetchError.message);
        setCredits(null);
      } else {
        setCredits(userCredits);
        setError(null);
      }

      // Fetch effective credits (may be team owner's credits)
      const { data: effective, error: effectiveError } = await supabase.rpc(
        "get_effective_credits",
        { p_user_id: user.id }
      );

      if (effectiveError) {
        console.error("Error fetching effective credits:", effectiveError);
      } else if (effective && effective.length > 0) {
        setEffectiveCredits({
          credits_balance: effective[0].credits_balance,
          is_team_credits: effective[0].is_team_credits,
          team_name: effective[0].team_name,
        });
      }
    } catch (err) {
      console.error("Error in fetchCredits:", err);
      setError("Failed to fetch credits");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const useCredit = async (): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      const { data, error: useError } = await supabase.rpc("use_credit", {
        p_user_id: user.id,
      });

      if (useError) {
        console.error("Error using credit:", useError);
        return false;
      }

      // Refresh credits after using one
      await fetchCredits();
      return data === true;
    } catch (err) {
      console.error("Error in useCredit:", err);
      return false;
    }
  };

  // Use effective credits balance (team or personal) to determine if user can analyze
  const availableBalance = effectiveCredits?.credits_balance ?? credits?.credits_balance ?? 0;
  const hasCredits = availableBalance > 0;
  const isUsingTeamCredits = effectiveCredits?.is_team_credits ?? false;
  const teamName = effectiveCredits?.team_name ?? null;

  return {
    credits,
    effectiveCredits,
    loading,
    error,
    hasCredits,
    availableBalance,
    isUsingTeamCredits,
    teamName,
    useCredit,
    refetch: fetchCredits,
    isAuthenticated,
  };
}
